/**
 * RichTextEditor — a small Tiptap editor used for trilingual post bodies.
 *
 * Emits HTML via onChange. The HTML is sanitised again server-side (nh3) on
 * save, so this editor only needs to be a comfortable authoring surface.
 *
 * `dir` switches the editor to RTL for the Arabic body.
 */
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Undo2,
} from "lucide-react";
import { useEffect } from "react";

import { cn } from "../lib/cn";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  dir?: "ltr" | "rtl";
  placeholder?: string;
}

export function RichTextEditor({
  value,
  onChange,
  dir = "ltr",
  placeholder = "Write here…",
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        dir,
        class: "max-w-none",
      },
    },
  });

  // Sync external value changes (e.g. when a post loads), but never while the
  // user is actively typing — that would fight the cursor.
  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (!editor.isFocused && incoming !== editor.getHTML()) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="h-40 rounded-md border border-stone-300 bg-stone-50" />
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-stone-300 bg-white focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-stone-200 bg-stone-50 px-1.5 py-1">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Bold"><Bold className="size-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Italic"><Italic className="size-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Heading 2"><Heading2 className="size-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Heading 3"><Heading3 className="size-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Bullet list"><List className="size-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Numbered list"><ListOrdered className="size-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Quote"><Quote className="size-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => setLink(editor)} active={editor.isActive("link")} label="Add link"><LinkIcon className="size-4" /></ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} label="Undo"><Undo2 className="size-4" /></ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} label="Redo"><Redo2 className="size-4" /></ToolBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function setLink(editor: ReturnType<typeof useEditor>) {
  if (!editor) return;
  const prev = editor.getAttributes("link").href as string | undefined;
  const url = window.prompt("Link URL (leave empty to remove)", prev ?? "https://");
  if (url === null) return;
  if (url.trim() === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
}

function ToolBtn({
  onClick,
  active,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "inline-flex size-7 items-center justify-center rounded text-stone-600 hover:bg-stone-200",
        active && "bg-red-100 text-red-700",
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-stone-200" aria-hidden="true" />;
}
